/**
 * ChangePasswordModal — self-serve password change for authenticated users.
 *
 * Opened from the header user menu ("Account settings"). Requires the user
 * to type their CURRENT password (prevents session-hijack password rotation)
 * plus a new password + confirmation.
 *
 * If the user forgot their current password, the "Forgot it?" link triggers
 * the standard /auth/forgot-password flow using their already-known account
 * email — so a logged-in user without their old password can still get
 * unstuck without logging out first.
 *
 * Backend: POST /api/auth/change-password (main flow)
 *          POST /api/auth/forgot-password (fallback)
 */
import { useState } from "react";
import { X, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export function ChangePasswordModal({ open, onClose }) {
  const { user } = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sendingReset, setSendingReset] = useState(false);

  if (!open) return null;

  const reset = () => { setCurrent(""); setNext(""); setConfirm(""); };
  const dismiss = () => { reset(); onClose(); };

  const submit = async (e) => {
    e.preventDefault();
    if (next !== confirm) { toast.error("New passwords don't match"); return; }
    if (next.length < 6)   { toast.error("New password must be at least 6 characters"); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/change-password", {
        current_password: current, new_password: next,
      });
      toast.success(data.message || "Password updated");
      dismiss();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't update password");
    } finally { setSubmitting(false); }
  };

  /** Kick off a password-reset email to the logged-in user's own address.
      Useful when the user is authenticated (session-cookied or via saved
      credentials) but can't remember what to type in "Current password". */
  const sendResetEmail = async () => {
    if (!user?.email) { toast.error("Couldn't find your account email"); return; }
    if (sendingReset) return;
    setSendingReset(true);
    try {
      await api.post("/auth/forgot-password", { email: user.email });
      toast.success(`Password reset link sent to ${user.email}`);
      dismiss();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't send reset email");
    } finally { setSendingReset(false); }
  };

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 backdrop-blur-sm px-4"
      onClick={dismiss}
      data-testid="change-password-modal"
    >
      <div
        className="w-full max-w-md bg-white rounded-2xl shadow-2xl border border-black/5 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-black/5">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-brand/10 grid place-items-center">
              <ShieldCheck className="w-4 h-4 text-brand" strokeWidth={2} />
            </div>
            <div>
              <h2 className="text-base font-semibold text-charcoal-900">Change password</h2>
              <p className="text-[11px] font-mono text-charcoal-400">Account security</p>
            </div>
          </div>
          <button onClick={dismiss} className="text-charcoal-400 hover:text-charcoal-700 p-1" data-testid="change-password-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={submit} className="px-6 py-5 space-y-4">
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <span className="block text-[13px] font-medium text-charcoal-700">Current password</span>
              <button
                type="button"
                onClick={sendResetEmail}
                disabled={sendingReset}
                className="text-[12px] text-brand hover:underline disabled:opacity-50 inline-flex items-center gap-1"
                data-testid="cp-forgot-link"
              >
                {sendingReset ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
                {sendingReset ? "Sending…" : "Forgot it?"}
              </button>
            </div>
            <input
              type="password"
              value={current}
              autoFocus
              autoComplete="current-password"
              onChange={(e) => setCurrent(e.target.value)}
              data-testid="cp-current"
              className="w-full px-3 py-2 rounded-lg border border-black/10 text-sm bg-white focus:outline-none focus:border-charcoal-900/30 focus:ring-2 focus:ring-charcoal-900/5"
            />
          </div>
          <Field label="New password" testid="cp-new" type="password" value={next} onChange={setNext} hint="At least 6 characters." />
          <Field label="Confirm new password" testid="cp-confirm" type="password" value={confirm} onChange={setConfirm} />

          <div className="flex items-center justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={dismiss}
              className="px-4 py-2 text-sm text-charcoal-600 hover:text-charcoal-900"
              data-testid="cp-cancel"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || !current || !next || !confirm}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-charcoal-900 text-white text-sm font-medium hover:bg-charcoal-800 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="cp-submit"
            >
              {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
              Update password
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", hint, testid, autoFocus }) {
  return (
    <label className="block">
      <span className="block text-[13px] font-medium text-charcoal-700 mb-1.5">{label}</span>
      <input
        type={type}
        value={value}
        autoFocus={autoFocus}
        autoComplete={type === "password" ? "new-password" : "off"}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="w-full px-3 py-2 rounded-lg border border-black/10 text-sm bg-white focus:outline-none focus:border-charcoal-900/30 focus:ring-2 focus:ring-charcoal-900/5"
      />
      {hint ? <p className="mt-1 text-[11px] text-charcoal-400">{hint}</p> : null}
    </label>
  );
}
